import React from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { QrCode, ShieldCheck, Zap, ScanLine, ArrowRight, Check } from "lucide-react";
import { Button } from "@/components/ui/button";
import UseCaseSlideshow from "@/components/UseCaseSlideshow";
import PricingCalculator from "@/components/PricingCalculator";

export default function Landing() {
  return (
    <div className="min-h-screen bg-[#FAFAFA] text-[#09090B]">
      {/* Nav */}
      <header className="border-b border-[#E4E4E7] bg-white sticky top-0 z-20">
        <div className="max-w-7xl mx-auto px-6 h-16 flex items-center justify-between">
          <Link to="/" className="flex items-center" data-testid="landing-brand">
            <img
              src="/scanmint-logo.png"
              alt="ScanMint"
              className="h-10 w-auto select-none"
              draggable={false}
            />
          </Link>
          <div className="flex items-center gap-3">
            <Link to="/login" data-testid="landing-login">
              <Button variant="ghost" className="h-10">
                Sign in
              </Button>
            </Link>
            <Link to="/register" data-testid="landing-signup">
              <Button className="bg-[#0F8F4F] hover:bg-[#0A6B3B] h-10">Get started</Button>
            </Link>
          </div>
        </div>
      </header>

      {/* Hero */}
      <section className="max-w-7xl mx-auto px-6 pt-16 md:pt-24 pb-20 md:pb-32 grid md:grid-cols-12 gap-10 items-center">
        <div className="md:col-span-7">
          <div className="mono-tiny text-[#0F8F4F] mb-5" data-testid="hero-tagline">
            QR verification · built for properties
          </div>
          <motion.h1
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="font-display text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tighter leading-[1.04]"
          >
            Every vehicle. <br />
            Every scan.
            <br />
            <span className="text-[#0F8F4F]">Verified in a second.</span>
          </motion.h1>
          <p className="mt-6 max-w-xl text-base md:text-lg text-[#71717A] leading-relaxed">
            ScanMint is a QR-based verification platform for property managers, restaurants, and
            businesses. Generate unique QR stickers tied to real-world assets and verify them
            instantly from any phone.
          </p>
          <div className="mt-8 flex flex-col sm:flex-row gap-3">
            <Link to="/register" data-testid="hero-cta-start">
              <Button className="bg-[#0F8F4F] hover:bg-[#0A6B3B] h-12 px-6 text-base">
                Start for $99 <ArrowRight className="ml-2 w-4 h-4" />
              </Button>
            </Link>
            <a href="#pricing" data-testid="hero-cta-pricing">
              <Button variant="outline" className="h-12 px-6 text-base border-[#E4E4E7]">
                See Pricing
              </Button>
            </a>
          </div>

          <div className="mt-10 grid grid-cols-3 gap-6 max-w-md">
            {[
              { k: "$1.00", v: "Per car / mo" },
              { k: "FREE", v: "First 30 days" },
              { k: "0", v: "Long-term contracts" },
            ].map((i) => (
              <div key={i.v} className="border-l-2 border-[#0F8F4F] pl-4">
                <div className="font-display text-2xl font-bold">{i.k}</div>
                <div className="mono-tiny text-[#71717A] mt-1">{i.v}</div>
              </div>
            ))}
          </div>
        </div>

        {/* QR preview */}
        <div className="md:col-span-5">
          <motion.div
            initial={{ opacity: 0, scale: 0.96 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.6, delay: 0.1 }}
            className="relative bg-white border border-[#E4E4E7] rounded-2xl p-8 shadow-sm"
          >
            <div className="mono-tiny text-[#71717A]">Live preview · Parkview Residences</div>
            <div className="mt-5 aspect-square relative overflow-hidden rounded-xl border border-[#E4E4E7] bg-[#FAFAFA] flex items-center justify-center">
              <svg viewBox="0 0 100 100" className="w-48 h-48">
                <rect width="100" height="100" fill="#fff" />
                {Array.from({ length: 20 }).map((_, i) =>
                  Array.from({ length: 20 }).map((__, j) => {
                    const on = (i * 7 + j * 13 + (i ^ j)) % 3 === 0;
                    return on ? (
                      <rect
                        key={`${i}-${j}`}
                        x={i * 5}
                        y={j * 5}
                        width="5"
                        height="5"
                        fill="#0F8F4F"
                      />
                    ) : null;
                  })
                )}
              </svg>
              <div className="scan-beam" />
            </div>
            <div className="mt-5 flex items-center justify-between">
              <div>
                <div className="font-display font-bold">2022 Tesla Model 3</div>
                <div className="mono-tiny text-[#71717A]">Plate · 8SCM221 · CA</div>
              </div>
              <div className="bg-[#00C16E]/10 text-[#008A4F] border border-[#00C16E]/20 px-3 py-1 rounded-full text-xs font-semibold">
                ACTIVE
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Use-case slideshow (Apartments → Restaurants → Barbers → Private) */}
      <UseCaseSlideshow />

      {/* Social-proof stats — hardcoded starter values; replace with admin
          settings + live aggregates later. */}
      <section className="border-t border-[#E4E4E7] bg-[#0F8F4F]/5">
        <div className="max-w-7xl mx-auto px-6 py-16 md:py-20">
          <div className="text-center max-w-2xl mx-auto">
            <div className="mono-tiny text-[#0F8F4F]">Trusted at scale</div>
            <h2 className="mt-3 font-display text-3xl md:text-4xl font-bold tracking-tight">
              Thousands of vehicle checks made simple.
            </h2>
            <p className="mt-3 text-[#3F3F46] text-sm md:text-base">
              Built for properties that need fast, reliable parking
              verification.
            </p>
          </div>
          <div className="mt-12 grid grid-cols-1 md:grid-cols-3 gap-6">
            {[
              { value: "2,400+", label: "Vehicles verified daily", testid: "stat-vehicles-verified" },
              { value: "180+", label: "Properties using ScanMint", testid: "stat-properties" },
              { value: "95+", label: "Businesses trusting ScanMint", testid: "stat-businesses" },
            ].map((s) => (
              <div
                key={s.testid}
                className="bg-white border border-[#E4E4E7] rounded-xl p-6 md:p-8 text-center"
                data-testid={s.testid}
              >
                <div className="font-display text-4xl md:text-5xl font-bold text-[#0F8F4F]">
                  {s.value}
                </div>
                <div className="mt-2 text-sm font-medium text-[#3F3F46]">
                  {s.label}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Feature grid */}
      <section className="border-t border-[#E4E4E7] bg-white">
        <div className="max-w-7xl mx-auto px-6 py-16 md:py-24">
          <div className="mono-tiny text-[#71717A]">The ScanMint stack</div>
          <h2 className="mt-3 font-display text-3xl md:text-4xl font-bold tracking-tight max-w-2xl">
            Everything you need to run verification at scale.
          </h2>

          <div className="mt-12 grid md:grid-cols-3 gap-6">
            {[
              {
                icon: QrCode,
                title: "Color + style QR",
                body: "Pick the sticker color and style that matches your brand. Unique per vehicle.",
              },
              {
                icon: Zap,
                title: "Activate in one tap",
                body: "Save & Activate. Prepaid monthly. Cancel anytime. No long contracts.",
              },
              {
                icon: ShieldCheck,
                title: "Privacy-safe scans",
                body: "Public scan pages reveal exactly what's needed for verification — vehicle, unit, and tenant contact when active. Hidden during pre-enrollment or suspension.",
              },
              {
                icon: ScanLine,
                title: "Full scan history",
                body: "Every scan is logged. See who, when, and from where. Export anytime.",
              },
              {
                icon: Check,
                title: "Multi-property",
                body: "One account, many properties. Switch with one click in the sidebar.",
              },
              {
                icon: Check,
                title: "Restaurant-ready",
                body: "Tables and menu QR codes coming in Restaurant Mode.",
              },
            ].map((f) => (
              <div
                key={f.title}
                className="border border-[#E4E4E7] rounded-lg p-6 hover:shadow-md transition-shadow bg-white"
                data-testid={`feature-${f.title.split(" ")[0].toLowerCase()}`}
              >
                <f.icon className="w-5 h-5 text-[#0F8F4F]" strokeWidth={1.5} />
                <div className="mt-4 font-display text-lg font-bold">{f.title}</div>
                <p className="mt-2 text-sm text-[#71717A] leading-relaxed">{f.body}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Pricing */}
      <section className="border-t border-[#E4E4E7]" id="pricing">
        <div className="max-w-7xl mx-auto px-6 py-16 md:py-24">
          <div className="mono-tiny text-[#71717A]">Simple, transparent pricing</div>
          <h2 className="mt-3 font-display text-3xl md:text-4xl font-bold tracking-tight">
            One plan. Built to scale with you.
          </h2>
          <p className="mt-3 text-[#3F3F46] text-sm md:text-base max-w-2xl">
            $99 to get started, your first month is on us, then $1.00 per car
            with volume discounts as you grow.
          </p>

          <div className="mt-10 grid md:grid-cols-5 gap-6 items-stretch">
            {/* Plan card */}
            <div
              className="md:col-span-3 bg-[#09090B] text-white rounded-xl p-8 md:p-10 relative overflow-hidden"
              data-testid="plan-scanmint"
            >
              <div className="absolute top-4 right-4 bg-[#0F8F4F] text-xs font-semibold px-3 py-1 rounded-full">
                FIRST MONTH FREE
              </div>
              <div className="mono-tiny text-white/60">ScanMint</div>
              <div className="mt-2 font-display text-4xl md:text-5xl font-bold leading-none">
                $1.00
                <span className="text-base text-white/60 font-medium">
                  {" "}/ car / mo
                </span>
              </div>
              <p className="text-sm text-white/70 mt-3">
                + <span className="font-bold text-white">$99 one-time setup fee</span>
                <span className="text-white/60"> (printer, sticker supply &amp; welcome packet)</span>
              </p>
              <p className="text-sm text-white/70 mt-1">
                <span className="font-bold text-white">First 30 days FREE</span>{" "}
                <span className="text-white/60">after the setup fee is paid.</span>
              </p>
              <ul className="mt-6 space-y-2 text-sm">
                <li className="flex gap-2" data-testid="plan-feature-tier1">
                  <Check className="w-4 h-4 text-[#00C16E] shrink-0 mt-0.5" />
                  1–199 cars · <b className="text-white">$1.00 / car</b>
                </li>
                <li className="flex gap-2" data-testid="plan-feature-tier2">
                  <Check className="w-4 h-4 text-[#00C16E] shrink-0 mt-0.5" />
                  200–499 cars · <b className="text-white">$0.90 / car</b>{" "}
                  <span className="text-white/60">(10% off)</span>
                </li>
                <li className="flex gap-2" data-testid="plan-feature-tier3">
                  <Check className="w-4 h-4 text-[#00C16E] shrink-0 mt-0.5" />
                  500–999 cars · <b className="text-white">$0.85 / car</b>{" "}
                  <span className="text-white/60">(15% off)</span>
                </li>
                <li className="flex gap-2" data-testid="plan-feature-tier4">
                  <Check className="w-4 h-4 text-[#00C16E] shrink-0 mt-0.5" />
                  1000+ cars · <b className="text-white">Request a quote</b>
                </li>
                <li className="flex gap-2">
                  <Check className="w-4 h-4 text-[#00C16E] shrink-0 mt-0.5" />
                  Unlimited properties &amp; tenants · ACH &amp; card via Stripe · Prepaid monthly · cancel anytime
                </li>
              </ul>
              <div className="mt-8">
                <Link to="/register" data-testid="pricing-cta-start">
                  <Button className="bg-white text-[#09090B] hover:bg-white/90 h-11 px-5">
                    Start for $99 <ArrowRight className="ml-2 w-4 h-4" />
                  </Button>
                </Link>
              </div>
            </div>

            {/* Example math card */}
            <div
              className="md:col-span-2 bg-white border border-[#E4E4E7] rounded-xl p-6 md:p-8 flex flex-col"
              data-testid="pricing-examples"
            >
              <div className="mono-tiny text-[#0F8F4F]">How it adds up</div>
              <h3 className="mt-2 font-display text-2xl font-bold tracking-tight">
                Examples
              </h3>
              <p className="text-sm text-[#71717A] mt-2">
                Every active car counts toward your monthly total. The bigger
                your fleet, the lower your per-car rate.
              </p>

              <div className="mt-6 divide-y divide-[#E4E4E7] border border-[#E4E4E7] rounded-lg overflow-hidden">
                <div
                  className="grid grid-cols-3 items-center px-4 py-3 bg-[#FAFAFA]"
                  data-testid="example-row-1"
                >
                  <div className="text-sm text-[#3F3F46] col-span-2">
                    <span className="font-semibold text-[#09090B]">50 cars</span>
                    <span className="text-[#71717A]"> at $1.00 each</span>
                  </div>
                  <div className="text-right font-display font-bold text-lg text-[#0F8F4F]">
                    $50<span className="text-xs text-[#71717A] font-medium"> / mo</span>
                  </div>
                </div>
                <div
                  className="grid grid-cols-3 items-center px-4 py-3"
                  data-testid="example-row-2"
                >
                  <div className="text-sm text-[#3F3F46] col-span-2">
                    <span className="font-semibold text-[#09090B]">200 cars</span>
                    <span className="text-[#71717A]"> at $0.90 each</span>
                    <span className="ml-2 text-[10px] uppercase tracking-wider text-[#0F8F4F] bg-[#0F8F4F]/10 border border-[#0F8F4F]/30 px-1.5 py-0.5 rounded">
                      10% OFF
                    </span>
                  </div>
                  <div className="text-right font-display font-bold text-lg text-[#0F8F4F]">
                    $180<span className="text-xs text-[#71717A] font-medium"> / mo</span>
                  </div>
                </div>
                <div
                  className="grid grid-cols-3 items-center px-4 py-3 bg-[#FAFAFA]"
                  data-testid="example-row-3"
                >
                  <div className="text-sm text-[#3F3F46] col-span-2">
                    <span className="font-semibold text-[#09090B]">500 cars</span>
                    <span className="text-[#71717A]"> at $0.85 each</span>
                    <span className="ml-2 text-[10px] uppercase tracking-wider text-[#0F8F4F] bg-[#0F8F4F]/10 border border-[#0F8F4F]/30 px-1.5 py-0.5 rounded">
                      15% OFF
                    </span>
                  </div>
                  <div className="text-right font-display font-bold text-lg text-[#0F8F4F]">
                    $425<span className="text-xs text-[#71717A] font-medium"> / mo</span>
                  </div>
                </div>
                <div
                  className="grid grid-cols-3 items-center px-4 py-3"
                  data-testid="example-row-4"
                >
                  <div className="text-sm text-[#3F3F46] col-span-2">
                    <span className="font-semibold text-[#09090B]">1000+ cars</span>
                    <span className="text-[#71717A]"> · enterprise</span>
                  </div>
                  <div className="text-right font-display font-bold text-base text-[#0F8F4F]">
                    Request a quote
                  </div>
                </div>
              </div>

              <div className="mt-5 text-xs text-[#71717A] leading-relaxed">
                Volume discounts are <b className="text-[#09090B]">flat-tier</b> — the entire
                fleet is billed at the bucket rate. Crossing into a discount
                lowers the price on <em>every</em> car, not just the new ones.
              </div>
            </div>
          </div>

          {/* Interactive calculator */}
          <PricingCalculator />
        </div>
      </section>

      <footer className="border-t border-[#E4E4E7] bg-white">
        <div className="max-w-7xl mx-auto px-6 py-10 flex flex-col md:flex-row items-center md:items-start justify-between gap-6">
          <div className="flex items-center gap-2">
            <div className="w-6 h-6 rounded bg-[#0F8F4F] flex items-center justify-center">
              <QrCode className="w-3 h-3 text-white" />
            </div>
            <span className="font-display font-bold text-sm">ScanMint</span>
          </div>
          <nav
            className="flex flex-wrap items-center gap-x-6 gap-y-2 text-sm text-[#3F3F46]"
            data-testid="landing-footer-legal"
          >
            <Link
              to="/legal/privacy"
              className="hover:text-[#0F8F4F] transition-colors"
              data-testid="footer-link-privacy"
            >
              Privacy Policy
            </Link>
            <Link
              to="/legal/terms"
              className="hover:text-[#0F8F4F] transition-colors"
              data-testid="footer-link-terms"
            >
              Terms of Service
            </Link>
            <Link
              to="/legal/refund"
              className="hover:text-[#0F8F4F] transition-colors"
              data-testid="footer-link-refund"
            >
              Refund &amp; Cancellation
            </Link>
          </nav>
          <div className="mono-tiny text-[#71717A]">© 2026 ScanMint · All rights reserved</div>
        </div>
      </footer>
    </div>
  );
}
